import * as Yup from 'yup';
import { useFormik } from 'formik';
import React, { HTMLAttributes, useEffect, useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoComplete from '@mui/material/Autocomplete';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  InsightViewAttributeItem,
  InsightViewVarianceDisplayType,
  InsightViewVarianceGroupDisplayType,
  InsightViewVarianceItem,
} from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { InsightViewDataSetType } from 'constant/ViewConstant';
import { INSIGHT_COLUM_GROUP_TOP_HEADER_TYPE } from './SelectGroupType';

const autoCompleteStyle = {
  '& .MuiAutocomplete-popper': {
    backgroundColor: 'red !imoprtant',
    fontSize: '10px',
  },
};
const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 550,
  bgcolor: 'background.paper',
  maxHeight: 450,
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalAddVarianceDefinitionProps {
  visible?: boolean;
  onClose?: () => void;
  onSave?: (attributeItem: InsightViewVarianceItem) => void;
  onDelete?: (attributeItem: InsightViewVarianceItem) => void;
  selectedData?: InsightViewVarianceItem;
  attributeData: InsightViewAttributeItem[];
  dataSetType?: string;
}

export type FormikVarianceValues = Omit<InsightViewVarianceItem, 'id'>;

export const formikInsightAttributeInitialValues: FormikVarianceValues = {
  group: null,
  resultColumn: null,
  targetColumn: null,
  groupDisplayType: InsightViewVarianceGroupDisplayType.EXISTING_GROUP,
  varianceDisplayType: InsightViewVarianceDisplayType.BOTH,
  headerName: '',
  displayName: '',
  weight: null,
};

export function buildVarianceColumnOptions(
  attributeData: InsightViewAttributeItem[],
  excludedColumnId?: string | number | null
): AutoCompleteItem[] {
  const groupNamesById = new Map(attributeData.filter((item) => item.isGroup).map((item) => [item.id, item.name]));

  return attributeData
    .filter((item) => !item.isGroup && item.id !== excludedColumnId?.toString())
    .map((item) => {
      const groupId = item.group?.value?.toString();
      const groupName = (groupId && groupNamesById.get(groupId)) || item.group?.label;
      const columnName = item.displayName?.trim() || item.name;

      return {
        label: groupName ? `${groupName} - ${columnName}` : columnName,
        value: item.id,
      };
    });
}

export function buildVarianceGroupOptions(attributeData: InsightViewAttributeItem[]): AutoCompleteItem[] {
  return attributeData
    .filter((item) => item.isGroup)
    .map((item) => ({
      label: item.name,
      value: item.id,
    }));
}

export function ModalAddVariance(props: Readonly<ModalAddVarianceDefinitionProps>) {
  const { onClose, visible = false, onSave, onDelete, selectedData, attributeData, dataSetType } = props;

  const formikValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        group: Yup.object().required('This field is required').nullable(),
        resultColumn: Yup.object().required('This field is required').nullable(),
        targetColumn: Yup.object().required('This field is required').nullable(),
        groupDisplayType: Yup.string()
          .oneOf(Object.values(InsightViewVarianceGroupDisplayType))
          .required('This field is required'),
        varianceDisplayType: Yup.string()
          .oneOf(Object.values(InsightViewVarianceDisplayType))
          .required('This field is required'),
        headerName: Yup.string().max(255, 'Maximum 255 characters allowed').nullable(),
        displayName: Yup.string().max(255, 'Maximum 255 characters allowed').nullable(),
        weight: Yup.number()
          .typeError('Weight must be a number')
          .min(0, 'Weight must be between 0 and 1')
          .max(1, 'Weight must be between 0 and 1')
          .nullable(),
      }),
    []
  );

  const formik = useFormik<FormikVarianceValues>({
    initialValues: formikInsightAttributeInitialValues,
    validationSchema: formikValidationSchema,
    onSubmit: () => {},
  });
  const isWeightedVariance = formik.values.weight != null;

  const handleOnCloseModal = () => {
    onClose?.();
    formik.resetForm();
  };

  const handleOnCancel = () => {
    handleOnCloseModal();
  };
  const handleOnDelete = () => {
    onDelete?.(selectedData as InsightViewVarianceItem);
    handleOnCloseModal();
  };

  const handleOnChangeGroup = (event: React.ChangeEvent<{}>, newValue: AutoCompleteItem | null) => {
    formik.setFieldValue('group', newValue);
  };

  const handleOnSave = () => {
    const selectedGroupDefinition = attributeData.find((item) => item.id === formik.values.group?.value?.toString());
    const usesDedicatedVarianceGroup =
      selectedGroupDefinition?.groupTopHeaderType === INSIGHT_COLUM_GROUP_TOP_HEADER_TYPE.VARIANCE;
    const normalizedValues = {
      ...formik.values,
      groupDisplayType: usesDedicatedVarianceGroup
        ? InsightViewVarianceGroupDisplayType.EXISTING_GROUP
        : formik.values.groupDisplayType ?? InsightViewVarianceGroupDisplayType.EXISTING_GROUP,
      varianceDisplayType: formik.values.varianceDisplayType ?? InsightViewVarianceDisplayType.BOTH,
      headerName: usesDedicatedVarianceGroup ? null : formik.values.headerName?.trim() || null,
      displayName: formik.values.displayName?.trim() || null,
      weight: formik.values.weight ?? null,
    };

    if (selectedData) {
      onSave?.({
        ...selectedData,
        ...normalizedValues,
      });
      handleOnCloseModal();
      return;
    }

    if (normalizedValues.group && normalizedValues.targetColumn && normalizedValues.resultColumn) {
      onSave?.({
        id: `NEWVARIANCE-${Date.now()}`,
        ...normalizedValues,
      });
    }

    handleOnCloseModal();
  };

  const dataGroupDropdownOptions: AutoCompleteItem[] = useMemo(() => {
    if (attributeData) {
      return attributeData
        .filter((item) => item.isGroup && item.groupTopHeaderType !== INSIGHT_COLUM_GROUP_TOP_HEADER_TYPE.VARIANCE)
        .map((item) => ({
          label: item.name,
          value: item.id,
        }));
    }

    return [];
  }, [attributeData]);

  const varianceGroupDropdownOptions: AutoCompleteItem[] = useMemo(() => {
    return buildVarianceGroupOptions(attributeData);
  }, [attributeData]);

  const varianceColumnLabels = useMemo(
    () => new Map(buildVarianceColumnOptions(attributeData).map((option) => [option.value.toString(), option.label])),
    [attributeData]
  );

  const getVarianceColumnOptionLabel = (option: AutoCompleteItem) => {
    if (dataSetType === InsightViewDataSetType.COMBINED_DATA_SET) {
      return option.label;
    }
    return varianceColumnLabels.get(option.value.toString()) ?? option.label;
  };

  const targetColumnDropdownOptions: AutoCompleteItem[] = useMemo(() => {
    if (dataSetType === InsightViewDataSetType.COMBINED_DATA_SET) {
      return dataGroupDropdownOptions;
    }

    if (attributeData && formik.values.group) {
      return buildVarianceColumnOptions(attributeData, formik.values.resultColumn?.value);
    }

    return [];
  }, [attributeData, formik.values.group, formik.values.resultColumn, dataSetType, dataGroupDropdownOptions]);

  const resultColumnDropdownOptions: AutoCompleteItem[] = useMemo(() => {
    if (dataSetType === InsightViewDataSetType.COMBINED_DATA_SET) {
      return dataGroupDropdownOptions;
    }

    if (attributeData && formik.values.group) {
      return buildVarianceColumnOptions(attributeData, formik.values.targetColumn?.value);
    }

    return [];
  }, [attributeData, formik.values.group, formik.values.targetColumn, dataSetType, dataGroupDropdownOptions]);

  const renderOption = (props: HTMLAttributes<HTMLElement>, option: AutoCompleteItem) => {
    return (
      <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
        <span>{option.label}</span>
      </Box>
    );
  };

  const isValidForm = formikValidationSchema.isValidSync(formik.values);

  useEffect(() => {
    if (selectedData) {
      formik.setValues({
        ...formikInsightAttributeInitialValues,
        ...selectedData,
        groupDisplayType: selectedData.groupDisplayType ?? InsightViewVarianceGroupDisplayType.EXISTING_GROUP,
        varianceDisplayType: selectedData.varianceDisplayType ?? InsightViewVarianceDisplayType.BOTH,
        headerName: selectedData.headerName ?? '',
        displayName: selectedData.displayName ?? '',
        weight: selectedData.weight ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData]);

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle} px={2}>
          <Stack>
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                Variance Definition
              </Typography>
              <IconButton onClick={handleOnCloseModal} component='label'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Display Group
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Select any existing group, or use a Variance group to combine variance columns under one heading.
            </Typography>
            <AutoComplete
              clearIcon={null}
              size='small'
              value={formik.values.group}
              onChange={handleOnChangeGroup}
              onBlur={formik.handleBlur}
              getOptionLabel={(option: AutoCompleteItem) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={varianceGroupDropdownOptions}
              renderOption={renderOption}
              sx={autoCompleteStyle}
              renderInput={(params) => (
                <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                  <TextField {...params} placeholder='Select Display Group' name='group' />
                </Stack>
              )}
            />
          </Stack>

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Displayed Columns
            </Typography>
            {isWeightedVariance ? (
              <TextField
                disabled
                size='small'
                value='Weighted Var only'
                helperText='Weight is defined, so Var and Var % are not displayed.'
              />
            ) : (
              <TextField
                select
                size='small'
                name='varianceDisplayType'
                value={formik.values.varianceDisplayType ?? InsightViewVarianceDisplayType.BOTH}
                onChange={formik.handleChange}
              >
                <MenuItem value={InsightViewVarianceDisplayType.BOTH}>Var and Var %</MenuItem>
                <MenuItem value={InsightViewVarianceDisplayType.VAR_ONLY}>Var only</MenuItem>
                <MenuItem value={InsightViewVarianceDisplayType.VAR_PERCENTAGE_ONLY}>Var % only</MenuItem>
              </TextField>
            )}
          </Stack>

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Display Name
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Optional. Defaults to Var.
            </Typography>
            <TextField
              size='small'
              name='displayName'
              value={formik.values.displayName ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder='Var'
              error={formik.touched.displayName && Boolean(formik.errors.displayName)}
              helperText={formik.touched.displayName && formik.errors.displayName}
            />
          </Stack>

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Weight
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Optional. Enter a value from 0 to 1 to display only the Weighted Var column.
            </Typography>
            <TextField
              size='small'
              type='number'
              name='weight'
              value={formik.values.weight ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                formik.setFieldValue('weight', value === '' ? null : Number(value));
              }}
              onBlur={formik.handleBlur}
              placeholder='0.3'
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              error={formik.touched.weight && Boolean(formik.errors.weight)}
              helperText={formik.touched.weight && formik.errors.weight}
            />
          </Stack>

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Result Column
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Choose a result column from any group.
            </Typography>
            <AutoComplete
              clearIcon={null}
              size='small'
              value={formik.values.resultColumn}
              onChange={(event, newValue) => {
                formik.setFieldValue('resultColumn', newValue);
              }}
              onBlur={formik.handleBlur}
              getOptionLabel={getVarianceColumnOptionLabel}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={resultColumnDropdownOptions}
              renderOption={renderOption}
              sx={autoCompleteStyle}
              renderInput={(params) => (
                <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                  <TextField {...params} placeholder='Select Result Column' name='resultColumn' />
                </Stack>
              )}
            />
          </Stack>
          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Target Column
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Choose a target column from any group.
            </Typography>
            <AutoComplete
              clearIcon={null}
              size='small'
              value={formik.values.targetColumn}
              onChange={(event, newValue) => {
                formik.setFieldValue('targetColumn', newValue);
              }}
              onBlur={formik.handleBlur}
              getOptionLabel={getVarianceColumnOptionLabel}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={targetColumnDropdownOptions}
              renderOption={renderOption}
              sx={autoCompleteStyle}
              renderInput={(params) => (
                <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                  <TextField {...params} placeholder='Select Target Column' name='targetColumn' />
                </Stack>
              )}
            />
          </Stack>
          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              {selectedData && (
                <Button
                  onClick={handleOnDelete}
                  variant='outlined'
                  sx={{
                    textTransform: 'none',
                  }}
                  color='error'
                >
                  Delete
                </Button>
              )}
              <Button variant='main-table-panel-border' onClick={handleOnCancel}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' disabled={!isValidForm} onClick={() => handleOnSave()}>
                Save Variance
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </div>
  );
}
